import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useEffect } from 'react'
import { useTranslation } from '@kostbar/i18n'
import { useUpdateItem } from '../hooks/use-item-mutations'
import type { MenuItem } from '@/api/types'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'

const editItemSchema = z.object({
  overrideName: z.string().optional(),
  sortOrder: z.number().int(),
  active: z.boolean(),
})

type EditItemFormData = z.infer<typeof editItemSchema>

interface EditItemDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  menuId: number
  sectionId: number
  item: MenuItem
}

export function EditItemDialog({
  open,
  onOpenChange,
  menuId,
  sectionId,
  item,
}: EditItemDialogProps) {
  const { t: tMenus } = useTranslation('menus')
  const updateItem = useUpdateItem()

  const form = useForm<EditItemFormData>({
    resolver: zodResolver(editItemSchema),
    defaultValues: {
      overrideName: item.overrideName || '',
      sortOrder: item.sortOrder,
      active: item.active,
    },
  })

  // Reset form when item changes
  useEffect(() => {
    form.reset({
      overrideName: item.overrideName || '',
      sortOrder: item.sortOrder,
      active: item.active,
    })
  }, [item, form])

  const onSubmit = (data: EditItemFormData) => {
    const payload = {
      ...data,
      overrideName: data.overrideName || undefined,
    }

    updateItem.mutate(
      { menuId, sectionId, productId: item.productId, data: payload },
      {
        onSuccess: () => {
          onOpenChange(false)
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{tMenus('item.editTitle')}</DialogTitle>
          <DialogDescription>
            {tMenus('item.editDescription', { name: item.baseName })}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="rounded-lg border p-3 bg-muted/50">
              <p className="text-sm font-medium">{tMenus('item.product')}</p>
              <p className="text-sm text-muted-foreground">{item.baseName}</p>
              {item.internalName && item.internalName !== item.baseName && (
                <p className="text-xs text-muted-foreground/60">
                  {tMenus('itemRow.internalName')} {item.internalName}
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                {tMenus('item.sku')} {item.sku}
              </p>
            </div>

            <FormField
              control={form.control}
              name="overrideName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tMenus('item.customName')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={tMenus('item.customNamePlaceholder')}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {tMenus('item.customNameHint')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="sortOrder"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tMenus('item.sortOrder')}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      {...field}
                      onChange={(e) =>
                        field.onChange(parseInt(e.target.value) || 0)
                      }
                    />
                  </FormControl>
                  <FormDescription>{tMenus('item.sortHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>{tMenus('item.active')}</FormLabel>
                    <FormDescription>
                      {tMenus('item.inactiveHint')}
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={updateItem.isPending}
              >
                {tMenus('item.cancel')}
              </Button>
              <Button type="submit" disabled={updateItem.isPending}>
                {updateItem.isPending
                  ? tMenus('item.saving')
                  : tMenus('item.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
